// src/api/features/dashboard/types.ts

/**
 * Dashboard feature types.
 *
 * Admin dashboard:
 * - Aggregated counts (cards)
 * - Bookings summary (monthly)
 * - Leads summary (monthly)
 */

export type DashboardPeriod = {
  /** Month number 1..12 */
  month: number;
  /** Full year (e.g., 2026) */
  year: number;
};

/* =====================================
 * Cards
 * ===================================== */

export type AdminDashboardCards = {
  customers: number;
  tours: number;

  // stable keys for FE even if not implemented yet
  hotels: number;
  cruise: number;
  attractions: number;
  transfer: number;
};

/* =====================================
 * Bookings Summary (shape from backend)
 * ===================================== */

export type AdminBookingsTotals = {
  /** Monetary strings, e.g. "123.45" */
  subtotal: string;
  taxTotal: string;
  grandTotal: string;
  paidTotal: string;
  dueTotal: string;
};

export type AdminBookingsSummary = {
  total: number;

  /**
   * Status counts.
   * Keys are backend-defined (e.g., "PENDING", "CONFIRMED", "CANCELLED", etc.)
   */
  byStatus: Record<string, number>;

  totals: AdminBookingsTotals;
};

/* =====================================
 * Leads Summary
 * ===================================== */

export type AdminLeadsSummary = {
  total: number;

  /**
   * Status counts.
   * Keys are backend-defined (e.g., "NEW", "CONTACTED", "CLOSED", etc.)
   */
  byStatus: Record<string, number>;

  /**
   * Priority counts.
   * Keys are backend-defined; backend may use "NONE" for null.
   */
  byPriority: Record<string, number>;
};

/* =====================================
 * Admin Dashboard Summary (final payload)
 * ===================================== */

export type AdminDashboardSummaryResult = {
  period: DashboardPeriod;
  cards: AdminDashboardCards;
  bookings: AdminBookingsSummary;
  leads: AdminLeadsSummary;
};

/* =====================================
 * React Query Keys
 * ===================================== */

export const DashboardKeys = {
  adminSummary: (month?: number, year?: number, employeeId?: number) =>
    ["dashboard", "admin", "summary", { month, year, employeeId }] as const,
};
